/**
 * Milestones API — B-2026-0075
 *
 * Covers:
 *   - getMyMilestones: GET /users/me/milestones
 *   - getUserMilestones: GET /users/{uid}/milestones
 */

import { apiClient } from '@/services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MilestoneItem {
  /** Stable milestone identifier (e.g. 'first_eval'). */
  id: string;
  /** Short human-readable badge name. */
  label: string;
  /** Coaching copy for the earned state. */
  description: string;
  /** Frontend icon identifier (emoji or key). */
  icon_key: string;
  /** Unearned text: what the athlete needs to do. */
  requirement: string;
  /** True for personal-only milestones (streak). */
  is_private: boolean;
  /** True when the milestone has been achieved. */
  earned: boolean;
  /** ISO-8601 UTC timestamp when first earned; null when not earned. */
  achieved_at: string | null;
}

export interface MilestonesResponse {
  milestones: MilestoneItem[];
  /** Current consecutive-week evaluation streak (self-view only; 0 for uid view). */
  current_streak_weeks: number;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Fetch the calling user's full milestone list including private (streak) badges.
 */
export async function getMyMilestones(): Promise<MilestonesResponse> {
  const { data } = await apiClient.get<MilestonesResponse>('/users/me/milestones');
  return data;
}

/**
 * Fetch public milestone badges for another user.
 *
 * Returns 403 if the queried user shares no channel with the caller,
 * or an empty milestone list if the target hasn't opted in to any leaderboard.
 */
export async function getUserMilestones(uid: string): Promise<MilestonesResponse> {
  const { data } = await apiClient.get<MilestonesResponse>(`/users/${uid}/milestones`);
  return data;
}
